import EntitySelect from '@shared/components/forms/inputs/entity-select/entity-select.vue';
import FieldWrapper from '@shared/components/forms/inputs/field-wrapper/field-wrapper.vue';
import { Component, Watch } from 'vue-property-decorator';
import VSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { DateClickArg } from '@fullcalendar/interaction';
import { BookingEvent, PublicBookingEventData } from '@bookings/interfaces/booking-event';
import moment, { Moment } from 'moment';
import { ParsedEvent } from '@bookings/interfaces/calendar';
import ResourceCalendar from '../resource-calendar/resource-calendar.vue';
import Datepicker from '@shared/components/forms/inputs/datepicker/datepicker.vue';
import PageTitle from '@shared/layouts/components/page-title/page-title.vue';
import { extractOptions } from '@core/helpers/option-groups.helper';
import { PublicBookingFormData } from '@bookings/interfaces/booking';
import PublicBookingFormCalendarOptions from './public-booking-form-options';
import { mixins } from 'vue-class-component';
import { ScheduleSlotRange } from '@bookings/interfaces/schedule-slot-range';
import { CalendarHelper } from '@shared/helpers/calendar-helper';
import { Subscription } from 'rxjs';
import ModulesHelper from '@core/services/modules.helper';

@Component({
  name: 'bpc-public-booking-form',
  components: {
    'v-select': VSelect,
    'bpc-entity-select': EntitySelect,
    'bpc-field-wrapper': FieldWrapper,
    'bpc-resource-calendar': ResourceCalendar,
    'bpc-datepicker': Datepicker,
    'bpc-page-title': PageTitle,
  },
})
export default class PublicBookingForm extends mixins(PublicBookingFormCalendarOptions) {
  /**
   * Events
   */
  events: ParsedEvent[] = [];
  tmpEvents: ParsedEvent[] = [];
  excludedEvents: ParsedEvent[] = [];
  backgroundEvents: ParsedEvent[] = [];
  bookingData: PublicBookingFormData = null;
  bookingEventData: PublicBookingEventData = null;
  currentContactBookings: BookingEvent[] = [];

  /**
   * Vars for control if it's possible to do a booking event in the selected slot
   */
  minAndMaxDateCriterionError: boolean = false;
  conflictCriterionError: boolean = false;
  startHourCriterionError: boolean = false;
  durationCriterionError: boolean = false;
  subscriptionCriterionError: boolean = false;
  maxOngoingAndUpcomingBookingsCriterionError: boolean = false;
  minTimeBeforeTwoBookingsCriterionError: boolean = false;
  slotPriceCategoryCriterionError: boolean = false;

  slotsChecked: boolean = false;
  slots = [];

  apiLastEventsRequest: Subscription = null;
  apiLastConflictsRequest: Subscription = null;
  apiLastContactEventsRequest: Subscription = null;
  /**
   * Others attr
   */
  successText: string = 'global.generic.successful_action';
  optionsDebugMode = false;
  confirmButtonDisabled: boolean = false;
  cancelButtonDisabled: boolean = false;

  /* Lifecycle hooks */
  // created () { }
  // updated () { }
  // destroyed () { }
  mounted() {
    if (this.usingOnlineBookingV2) {
      this.$router.push({ name: 'profile-online-booking' });
      return null;
    }
    this.getSlotPricesCategories();
  }

  get usingOnlineBookingV2() {
    return this.$modulesService.hasModuleActivated(ModulesHelper.ONLINE_BOOKING_V2_MODULE_NAME);
  }

  get minimumBookingDate(): Moment {
    if (this.mergedBookingOptions && this.mergedBookingOptions.min_time_before_booking) {
      return moment().add(this.mergedBookingOptions.min_time_before_booking, 'minutes');
    }
  }

  get maximumBookingDate(): Moment {
    if (this.mergedBookingOptions && this.mergedBookingOptions.max_time_before_booking) {
      return moment().add(
        Number(this.mergedBookingOptions.max_time_before_booking) * 24 * 60,
        'minutes',
      );
    }
  }

  get allEvents(): ParsedEvent[] {
    return this.events.concat(
      this.excludedEvents,
      this.tmpEvents,
      this.slots,
      this.backgroundEvents,
    );
  }

  get eventQuery() {
    return {
      status: ['confirmed', 'requested'],
      page: 1,
      per_page: -1,
      orderBy: 'date',
      orderDirection: 'desc',
      date: this.selectedDate,
      resource_discipline_id: this.selectedDiscipline?.id,
      columns: [
        'id',
        'resource_id',
        'contact_id',
        'date',
        'start_time',
        'end_time',
        'center_id',
        'discipline_id',
        'is_multiplayer',
      ],
      relationFields: [
        'bookingEventMultiplayerData.number_of_players',
        'bookingEventMultiplayerData.id',
        'bookingEventMultiplayerData.booking_event_multiplayer_id',
      ],
      withRelations: ['resource', 'discipline', 'center', 'orders.orderItems'],
    };
  }

  get readyToShowCalendar(): boolean {
    return (
      !!this.selectedDuration &&
      !!this.disciplineId &&
      !!this.selectedPaymentMethod &&
      this.resources.length > 0
    );
  }

  get maxNumberOfOngoingUpcomingBookingsWhenOnlinePayment(): number {
    return (
      this.mergedBookingOptions?.max_number_of_ongoing_upcoming_bookings_when_online_payment || 0
    );
  }

  get maxNumberOfOngoingUpcomingBookingsWhenSubscriptionPayment(): number {
    return (
      this.mergedBookingOptions
        ?.max_number_of_ongoing_upcoming_bookings_when_subscription_payment || 0
    );
  }
  get minTimeBeforeCancellation() {
    return 60 * (this.mergedBookingOptions?.min_time_before_cancellation || 0);
  }

  get minTimeBetweenTwoBookings(): number {
    return 60 * (this.mergedBookingOptions?.min_time_between_two_bookings || 0);
  }

  async getAllEvents(): Promise<void> {
    this.currentContactBookings = [];

    await this.getEvents();
    await this.getConflicts();
    await this.getCurrentContactEvents();
  }

  async getEvents() {
    return new Promise((resolve, reject) => {
      if (!this.selectedDate) {
        return;
      }
      const config = {
        params: this.eventQuery,
      };

      if (this.apiLastEventsRequest) {
        this.apiLastEventsRequest.unsubscribe();
      }
      this.apiLastEventsRequest = this.$bpcApi
        .get(`${this.$bpcApiRouteHelper.getEntityPath('booking-event')}/public-booking`, config)
        .subscribe(res => {
          this.events = res.data.map(event => {
            if (event.contact_id && Number(event.contact_id) === Number(this.currentContactId)) {
              return this.parseBookingEventToCalendarOwnEvent(event);
            }
            return this.parseBookingEventToCalendarNotAvailableEvent(event);
          });

          resolve(this.events);
        });
    });
  }

  async getCurrentContactEvents() {
    return new Promise((resolve, reject) => {
      const query = {
        status: ['confirmed', 'requested'],
        page: 1,
        per_page: 200,
        orderBy: 'date',
        orderDirection: 'desc',
        contact_id: this.currentContactId,
        date_from: moment()
          .subtract(7, 'days')
          .format('YYYY-MM-DD'),
        columns: [
          'id',
          'resource_id',
          'contact_id',
          'date',
          'start_time',
          'end_time',
          'center_id',
          'discipline_id',
        ],
        withRelations: ['resource', 'discipline', 'center', 'orders'],
        addOrderItemsWithSubscriptionIds: true,
      };

      const config = {
        params: query,
      };

      if (this.apiLastContactEventsRequest) {
        this.apiLastContactEventsRequest.unsubscribe();
      }
      this.apiLastContactEventsRequest = this.$bpcApi
        .get(`${this.$bpcApiRouteHelper.getEntityPath('booking-event')}/public-booking`, config)
        .subscribe(res => {
          this.currentContactBookings = res.data;
          resolve(this.currentContactBookings);
        });
    });
  }

  get currentSlotDuration(): number {
    return this.$store.getters['centerSettings/centerSettings']?.default_slot_duration || 30;
  }

  get currentSlotMinTime(): string {
    return this.$store.getters['centerSettings/centerSettings']?.default_start_hour;
  }

  get currentSlotMaxTime(): string {
    return this.$store.getters['centerSettings/centerSettings']?.default_end_hour;
  }

  get todayStartTime(): string {
    const start = moment(`${this.selectedDate} ${this.currentSlotMinTime}`, 'YYYY-MM-DD HH:mm:ss');
    const startTimes = this.mergedBookingOptions.start_time.split([',']);
    let optionStartTime = null;
    if (startTimes.length === 1 || (startTimes.length > 0 && startTimes.length !== 7)) {
      optionStartTime = moment(startTimes[0], 'h:m', true);
    } else if (startTimes.length === 7) {
      const day = start.weekday();
      if (day === 0) {
        optionStartTime = moment(startTimes[6], 'h:m', true);
      } else {
        optionStartTime = moment(startTimes[day - 1], 'h:m', true);
      }
    }
    return optionStartTime.format('hh:mm');
  }

  get shoppingCartActivated(): boolean {
    return this.$store.getters['centerSettings/shoppingCartActivated'];
  }

  checkSlotsAvailability() {
    this.slotsChecked = false;
    const checkSlots = [];
    const savedMergedBookingOptions = this.mergedBookingOptions;

    // Check all resources
    this.rawResources.forEach(resource => {
      // Default hours to open and close center
      const start = moment(
        `${this.selectedDate} ${this.currentSlotMinTime}`,
        'YYYY-MM-DD HH:mm:ss',
      );
      const end = moment(`${this.selectedDate} ${this.currentSlotMaxTime}`, 'YYYY-MM-DD HH:mm:ss');
      const totalSlots = Math.ceil(end.diff(start, 'minutes') / this.currentSlotDuration);
      this.mergedBookingOptions = extractOptions(this.bookingOptions, {
        resources: resource.id,
        subscription_categories: this.selectedSubscriptionCategory?.id,
      });

      // Check all slots by resource
      for (let init = 0; init < totalSlots; init++) {
        const endSlot = moment(start).add(this.currentSlotDuration, 'minutes');

        const resourceId = resource.id;
        const duration = this.selectedDuration?.value;
        const stepDuration = this.mergedBookingOptions?.step_duration;
        const onlyWithSubscriptions = this.onlyWithSubscriptions();

        const bestValidStartTime = this.getBestValidStartTime(
          resourceId,
          start,
          duration,
          stepDuration,
          onlyWithSubscriptions,
        );

        if (bestValidStartTime) {
          checkSlots.push(
            this.parseSlotAvailableToCalendar(
              this.selectedDate,
              start.format('HH:mm'),
              endSlot.format('HH:mm'),
              resource,
            ),
          );
        } else {
          checkSlots.push(
            this.parseSlotNotAvailableToCalendar(
              this.selectedDate,
              start.format('HH:mm'),
              endSlot.format('HH:mm'),
              resource,
            ),
          );
        }
        start.add(this.currentSlotDuration, 'minutes');
      }
    });

    this.slots = checkSlots;
    this.slotsChecked = true;
    console.debug(
      `Slots: ${this.slots.map(
        slot => `${slot.resourceId} - ${slot.start} - ${slot.end} - ${slot.className} \n`,
      )}`,
    );
    this.mergedBookingOptions = savedMergedBookingOptions;
    this.checkMaxOngoingAndUpcomingBookings();
  }

  parseSlotAvailableToCalendar(date, start, end, resource = null) {
    return {
      start: this.formatDateTime(date, start),
      end: this.formatDateTime(date, end),
      backgroundColor: CalendarHelper.PROFILE_EVENT_FREE_SLOT_BG_COLOR,
      borderColor: CalendarHelper.PROFILE_EVENT_FREE_SLOT_BORDER_COLOR,
      resourceId: `${resource.id}`,
      className: ['slot-available'],
      event_type: 'slot',
      display: 'background',
    };
  }

  parseSlotNotAvailableToCalendar(date, start, end, resource = null) {
    return {
      start: this.formatDateTime(date, start),
      end: this.formatDateTime(date, end),
      backgroundColor: CalendarHelper.PROFILE_EVENT_NOT_FREE_SLOT_BG_COLOR,
      borderColor: CalendarHelper.PROFILE_EVENT_NOT_FREE_SLOT_BORDER_COLOR,
      //backgroundColor: '#cccccc', // 'grey',
      //borderColor: '#cccccc', // 'grey',
      resourceId: `${resource.id}`,
      event_type: 'slot',
      className: ['slot-not-available'],
      display: 'background',
    };
  }

  async getConflicts() {
    return new Promise((resolve, reject) => {
      const config = {
        params: { ...this.eventQuery, resourceIds: this.resourceIds },
      };

      if (this.apiLastConflictsRequest) {
        this.apiLastConflictsRequest.unsubscribe();
      }
      this.apiLastConflictsRequest = this.$bpcApi
        .get(
          `${this.$bpcApiRouteHelper.getEntityPath('booking-event')}/resource-event-conflicts`,
          config,
        )
        .subscribe(res => {
          this.excludedEvents = res.data.map(excludedEvent => {
            return this.parseBookingEventToCalendarNotAvailableEvent(excludedEvent);
          });
          resolve(this.excludedEvents);
        });
    });
  }

  formatDateTime(date, time): string {
    return moment(`${date} ${time}`).toISOString();
  }

  parseBookingEventToCalendarNotAvailableEvent(event: BookingEvent): ParsedEvent {
    return {
      id: event?.id,
      // title: this.$t('global.booking_events.not_available'),
      start: this.formatDateTime(event.date, event.start_time),
      end: this.formatDateTime(event.date, event.end_time),
      backgroundColor: CalendarHelper.PROFILE_EVENT_CONFLICT_BG_COLOR, //'#0d3a5d', //'#d92550',
      borderColor: CalendarHelper.PROFILE_EVENT_CONFLICT_BORDER_COLOR, //'#0d3a5d', //'#d92550',
      resourceId: `${event?.resource_id}`,
      className: ['unavailable-event'],
      event_type: 'conflict',
      display: 'background',
    };
  }

  async saveBooking(data: PublicBookingFormData) {
    delete data.status;
    if (this.onlyWithSubscriptions()) {
      data.payWithSubscriptions = this.onlyWithSubscriptions();
    } else {
      delete data.payWithSubscriptions;
    }
    await this.$bpcApi
      .post(`${this.$bpcApiRouteHelper.getEntityPath('booking')}/public`, data)
      .toPromise()
      .then(async res => {
        this.slotsChecked = false;
        this.bookingData.status = res.data.status;
        this.$root.$emit('refresh-orders');
        this.$bvModal.hide('booking-confirmation-modal');
        this.$bvModal.show('success-booking-confirmation-modal');
        await this.getAllEvents();
        this.checkSlotsAvailability();
        this.confirmButtonDisabled = false;
      })
      .catch(err => {
        this.$bvModal.hide('booking-confirmation-modal');
        this.$bvModal.msgBoxOk(this.$t(err.response.data.message).toString(), {
          title: this.$t('global.modals.public_booking.not_possible.title').toString(),
          okTitle: this.$t('global.buttons.close').toString(),
          okVariant: 'secondary',
        });
        this.confirmButtonDisabled = false;
        console.log(err.message);
      });
  }

  onSlotClicked(info: DateClickArg) {
    if (!this.slotsChecked) {
      return;
    }
    const savedMergedBookingOptions = this.mergedBookingOptions;
    this.bookingData = null;
    const resourceId = info.resource?.id;
    if (!resourceId) {
      return;
    }

    this.mergedBookingOptions = extractOptions(this.bookingOptions, {
      resources: resourceId,
      subscription_categories: this.subscriptionCategoryId,
    }); // To use in the booking;

    const duration = this.selectedDuration?.value;
    const stepDuration = this.mergedBookingOptions?.step_duration;
    const onlyWithSubscriptions = this.onlyWithSubscriptions();

    const selectedTime = this.getBestValidStartTime(
      resourceId,
      info.dateStr,
      duration,
      stepDuration,
      onlyWithSubscriptions,
    );

    if (selectedTime) {
      const end = moment(selectedTime).add(duration, 'minutes');

      const canBeBooked = this.canBeBooked(
        selectedTime,
        end,
        resourceId,
        duration,
        onlyWithSubscriptions,
      );

      if (canBeBooked) {
        this.bookingData = this.generateBookingData(selectedTime, end, resourceId, info.resource);
        this.tmpEvents.push(this.parseBookingDataToCalendarTmpEvent(this.bookingData));
        this.showBookingConfirmationModal();
      } else {
        this.showBookingNotPosibleModal();
      }
    } else {
      this.showBookingNotPosibleModal();
    }

    this.mergedBookingOptions = savedMergedBookingOptions;
  }

  /**
   * Method to calculate the best start time depending on the resource, the selected duration,
   * the steps duration between each slots and all the configured rules to allow or not to book
   * in this specific slot.
   *
   * This method tries to get a startTime (moment object) where the generated time
   * range (from startTime to startTime + duration) contains the dateTimeStr and is
   * available to book. It choose the dateTime closer to the dateTimeStr
   */
  getBestValidStartTime(
    resourceId: number | string,
    dateTimeStr: string | moment.Moment,
    duration: number,
    stepDuration: number,
    onlyWithSubscriptions: boolean,
  ): moment.Moment | null {
    const datesToTry = this.validStartTimes(dateTimeStr, duration, stepDuration);

    const selectedDate = datesToTry.find(start => {
      const end = moment(start).add(duration, 'minutes');
      // The method is used to check if a slot can be booked and display as available or not. We want to separate the rules
      // we have to apply in order to check the slots available and if it can be booked or not
      console.debug(`== ${resourceId} - ${start} - ${end}`);
      console.debug(
        `${this.checkDurationCriterion(duration)} + ${this.checkStartHourCriterion(
          start,
        )} + ${this.checkMinAndMaxDateCriterion(start, end)} + ${this.checkConflicCriterion(
          start,
          end,
          resourceId,
        )} + ${this.checkSubscriptionCriterion(
          onlyWithSubscriptions,
        )} + ${this.checkMinTimeBetweenTwoBookings(start)} + ${this.checkSlotPriceCategoryCriterion(
          start,
          duration,
          resourceId,
        )}`,
      );
      return (
        this.checkDurationCriterion(duration) &&
        this.checkStartHourCriterion(start) &&
        this.checkMinAndMaxDateCriterion(start, end) &&
        this.checkConflicCriterion(start, end, resourceId) &&
        this.checkSubscriptionCriterion(onlyWithSubscriptions) &&
        this.checkMinTimeBetweenTwoBookings(start) &&
        this.checkSlotPriceCategoryCriterion(start, duration, resourceId)
      );
    });

    return selectedDate;
  }

  /**
   * This method check all the time intervals with duration = duration
   * separated from each other by the stepDuration which contains
   * the dateTimeStr inside. And returns the array of startTimes of
   * those intervals
   */
  validStartTimes(dateTimeStr, duration, stepDuration) {
    const prevSteps = Math.floor(duration / stepDuration);
    const initialDate = moment(dateTimeStr);
    const res = [];
    for (let i = 0; i < prevSteps; i++) {
      const start = moment(dateTimeStr).subtract(stepDuration * i, 'minutes');
      const dateToAdd = this.calculateStart(start);
      const intervalEnd = moment(dateToAdd).add(duration, 'minutes');
      if (initialDate.isSameOrAfter(dateToAdd) && initialDate.isSameOrBefore(intervalEnd)) {
        res.push(dateToAdd);
      } else {
        // Should not be here
        console.log(dateTimeStr, dateToAdd, intervalEnd);
      }
    }
    return res;
  }

  showBookingConfirmationModal() {
    this.$bvModal.show('booking-confirmation-modal');
  }

  onCloseConfirmBookingModal() {
    this.bookingData = null;
    this.cleanTmpEvents();
    this.$bvModal.hide('booking-confirmation-modal');
  }

  onConfirmBookingModal() {
    this.confirmButtonDisabled = true;
    this.cleanTmpEvents();
    this.saveBooking(this.bookingData);
  }
  onSuccessConfirmBookingModal() {
    this.$router.push({
      name: this.$clientRouter.getPaymentProcessRouteNameV2(),
    });
  }
  onCloseSuccessConfirmBookingModal() {
    this.$bvModal.hide('success-booking-confirmation-modal');
  }

  showBookingNotPosibleModal() {
    this.$bvModal.show('booking-not-posible-modal');
  }

  onCloseNotPosibleModal() {
    this.$bvModal.hide('booking-not-posible-modal');
  }

  showBookingEventDetailModal() {
    this.$bvModal.show('booking-detail-modal');
  }

  onCloseDetailBookingEventModal() {
    this.$bvModal.hide('booking-detail-modal');
  }

  minTimeBeforeCancellationCriterionErrorMessage: number = 0;

  async onCancelBookingEventModal() {
    const canBeCancelled = this.checkIfBookingCanBeCancelled(this.bookingEventData);
    if (canBeCancelled) {
      const value = await this.$bvModal.msgBoxConfirm(
        this.$t('global.modals.public_booking.cancellation.confirm_question').toString(),
        {
          title: this.$t('global.modals.public_booking.cancellation.title').toString(),
          cancelTitle: this.$t('global.buttons.close').toString(),
          okTitle: this.$t('global.buttons.confirm').toString(),
          okVariant: 'primary',
          cancelVariant: 'link',
        },
      );

      if (value) {
        this.cancelButtonDisabled = true;
        try {
          await this.cancelBookingEvent(this.bookingEventData);
          this.cancelButtonDisabled = false;
        } catch (error) {
          this.cancelButtonDisabled = false;
        }
      }
    } else {
      this.cancelButtonDisabled = false;
      const diffInMinutes = moment(
        `${this.bookingEventData.date} ${this.bookingEventData.start_time}`,
      ).diff(moment(), 'minutes');

      this.$bvModal.msgBoxOk(
        this.$t('global.modals.public_booking.cancellation.not_possible', {
          minTime: moment()
            .subtract(
              Math.abs(diffInMinutes - this.minTimeBeforeCancellationCriterionErrorMessage + 1),
              'minutes',
            )
            .format('DD/MM/YYYY HH:mm'),
        }).toString(),
        {
          title: this.$t('global.modals.public_booking.cancellation.title').toString(),
          okTitle: this.$t('global.buttons.close').toString(),
        },
      );
    }
  }

  async cancelBookingEvent(data: PublicBookingEventData) {
    const res = await this.$bpcApi
      .post(`${this.$bpcApiRouteHelper.getEntityPath('booking-event')}/public/cancel`, data)
      .toPromise();

    this.$bvModal.hide('booking-cancellation-modal');
    this.$bvModal.msgBoxOk(
      this.$t('global.modals.public_booking.cancellation.success_body').toString(),
      {
        title: this.$t('global.modals.public_booking.cancellation.title').toString(),
        okTitle: this.$t('global.buttons.close').toString(),
        okVariant: 'secondary',
      },
    );
    this.onCloseDetailBookingEventModal();
    this.slotsChecked = false;
    await this.getAllEvents();
    this.checkSlotsAvailability();
    this.cancelButtonDisabled = false;
  }

  checkIfBookingCanBeCancelled(bookingEventData: PublicBookingEventData): boolean {
    const savedMergedBookingOptions = this.mergedBookingOptions;

    this.mergedBookingOptions = extractOptions(this.bookingOptions, {
      resources: bookingEventData.resourceId,
      subscription_categories: this.selectedSubscriptionCategory?.id,
    });
    const diffInMinutes = moment(`${bookingEventData.date} ${bookingEventData.start_time}`).diff(
      moment(),
      'minutes',
    );
    this.mergedBookingOptions = savedMergedBookingOptions;
    return diffInMinutes >= this.minTimeBeforeCancellation;
  }

  allLoadedEventsByResource(resourceId: number | string): ParsedEvent[] {
    return this.allEvents.filter(event => {
      return Number(event.resourceId) === Number(resourceId) && event.event_type !== 'slot';
    });
  }

  allBackgroundEventsByResource(resourceId: number | string): ParsedEvent[] {
    return this.backgroundEvents.filter(event => Number(event.resourceId) === Number(resourceId));
  }

  canBeBooked(
    start: Moment,
    end: Moment,
    resourceId: number | string,
    duration: number,
    onlyWithSubscriptions: boolean = false,
  ): boolean {
    return (
      this.checkDurationCriterion(duration) &&
      this.checkStartHourCriterion(start) &&
      this.checkMinAndMaxDateCriterion(start, end) &&
      this.checkConflicCriterion(start, end, resourceId) &&
      this.checkSubscriptionCriterion(onlyWithSubscriptions) &&
      this.checkMaxOngoingAndUpcomingBookings() &&
      this.checkMinTimeBetweenTwoBookings(start) &&
      this.checkSlotPriceCategoryCriterion(start, duration, resourceId)
    );
  }

  minTimeBeforeTwoBookingsCriterionErrorMessage: number = 0;
  checkMinTimeBetweenTwoBookings(start: Moment): boolean {
    let isValid = true;
    const eventStartDate = moment(`${this.selectedDate} ${start.format('HH:mm:ss')}`);

    this.currentContactBookings
      // When paying with subscription, only consider bookings on resources accessible with that subscription
      .filter(
        event =>
          !this.selectedSubscriptionCategory ||
          this.selectedSubscriptionCategory.resources.some(
            possibleResource => possibleResource.id === event.resource_id,
          ),
      )
      // When paying with subscription, only consider bookings paid with the same subscription category
      .filter(
        event =>
          !this.selectedSubscriptionCategory ||
          this.isEventPaidWithSelectedSubscriptionCategory(event),
      )
      .forEach(event => {
        console.debug(
          `Checking event ${event.resource_id} - ${event.date} : ${event.start_time} -> ${event.end_time}`,
        );
        // Compare the slot start time with the event end time (if it is before) or start time (if it is after)
        const eventTime = moment(`${event.date} ${event.start_time}`).isBefore(eventStartDate)
          ? event.end_time
          : event.start_time;
        const diffTime = Math.abs(
          eventStartDate.diff(moment(`${event.date} ${eventTime}`), 'minutes'),
        );
        if (this.minTimeBetweenTwoBookings > 0 && diffTime < this.minTimeBetweenTwoBookings) {
          console.debug(
            `booking too close: ${event.date} - ${event.end_time}. ${diffTime} < ${this.minTimeBetweenTwoBookings}`,
          );
          isValid = false;
        }
      });

    this.minTimeBeforeTwoBookingsCriterionError = !isValid;

    if (!isValid) {
      this.minTimeBeforeTwoBookingsCriterionErrorMessage = this.minTimeBetweenTwoBookings;
    } else {
      this.minTimeBeforeTwoBookingsCriterionErrorMessage = 0;
    }

    return isValid;
  }

  checkMaxOngoingAndUpcomingBookings(): boolean {
    const currentEvents = this.currentContactBookings.filter(event => {
      return (
        ((event.date === moment().format('YYYY-MM-DD') &&
          event.end_time >= moment().format('HH:mm:ss')) ||
          event.date > moment().format('YYYY-MM-DD')) &&
        event.discipline_id === this.selectedDiscipline.id
      );
    });

    // Events paid with subscription
    const eventsWithSameSubscriptionId = currentEvents.filter(event => {
      return this.isEventPaidWithSelectedSubscriptionCategory(event);
    });

    // Events paid with online payment
    const eventsWithOnlinePayment = currentEvents.filter(event => {
      return (event?.orders || []).find(order => {
        if (!order.current) {
          return false;
        }
        const paidWithSubscription = (order?.order_items || []).some(orderItem => {
          return !!orderItem.subscription_id;
        });

        return !paidWithSubscription;
      });
    });

    let isValid = true;

    // If only need to be applied for payments with subscriptions
    if (this.selectedPaymentMethod?.value === 'subscription_only') {
      isValid =
        eventsWithSameSubscriptionId.length <
        this.maxNumberOfOngoingUpcomingBookingsWhenSubscriptionPayment;
    }

    // If only need to be applied for online payments
    if (this.selectedPaymentMethod?.value === 'payment_only') {
      isValid =
        eventsWithOnlinePayment.length < this.maxNumberOfOngoingUpcomingBookingsWhenOnlinePayment;
    }

    this.maxOngoingAndUpcomingBookingsCriterionError = !isValid;
    return isValid;
  }

  private isEventPaidWithSelectedSubscriptionCategory(event: BookingEvent) {
    return (event?.orders || []).find(order => {
      if (!order.current) {
        return false;
      }
      return (order?.order_items || []).find(orderItem => {
        if (orderItem.subscription_id) {
          return this.contactSubscriptionsOfSelectedSubscriptionCategory.find(subscription => {
            return orderItem.subscription_id === subscription.id;
          });
        } else {
          return false;
        }
      });
    });
  }

  checkSubscriptionCriterion(onlyWithSubscriptions): boolean {
    let isValid = true;

    if (onlyWithSubscriptions && !!this.selectedSubscriptionCategory === true) {
      const validSubscriptions = this.contactSubscriptionsOfSelectedSubscriptionCategory.filter(
        subscription => {
          return (
            subscription.start_date <= this.selectedDate &&
            subscription.end_date >= this.selectedDate
          );
        },
      );
      isValid = validSubscriptions.length > 0;
    } else if (onlyWithSubscriptions && !!this.selectedSubscriptionCategory === false) {
      isValid = false;
    } else if (
      !onlyWithSubscriptions &&
      this.mergedBookingOptions.only_with_subscriptions === 'subscription_only'
    ) {
      isValid = false;
    }
    this.subscriptionCriterionError = !isValid;
    return isValid;
  }

  minBookingDateCriterionErrorMessage: string = null;
  maxBookingDateCriterionErrorMessage: string = null;
  checkMinAndMaxDateCriterion(start: Moment, end: Moment): boolean {
    const now = moment();
    const isValid =
      start.isAfter(now) &&
      start.isSameOrAfter(this.minimumBookingDate) &&
      end.isSameOrBefore(this.maximumBookingDate);
    this.minAndMaxDateCriterionError = !isValid;

    if (!isValid) {
      this.minBookingDateCriterionErrorMessage = this.minimumBookingDate?.format(
        'DD/MM/YYYY HH:mm',
      );
      this.maxBookingDateCriterionErrorMessage = this.maximumBookingDate?.format(
        'DD/MM/YYYY HH:mm',
      );
    } else {
      this.minBookingDateCriterionErrorMessage = null;
      this.maxBookingDateCriterionErrorMessage = null;
    }

    return isValid;
  }

  checkSlotPriceCategoryCriterion(
    eventStart: Moment,
    duration: number,
    resourceId: number | string,
  ): boolean {
    const eventEnd = eventStart.clone().add(duration, 'minutes');

    const backgroundEventByResource = this.allBackgroundEventsByResource(resourceId);

    const slotsThatContainsTheEntireEvent = backgroundEventByResource.filter(slotEvent => {
      const slotStart = moment(slotEvent.start);
      const slotEnd = moment(slotEvent.end);

      // event  [--]    OR [-----]
      // slot  [-----]  OR [-----]
      return slotStart.isSameOrBefore(eventStart) && slotEnd.isSameOrAfter(eventEnd);
    });

    if (slotsThatContainsTheEntireEvent.length) {
      this.slotPriceCategoryCriterionError = false;
      return true;
    }

    const totalSlots = Math.ceil(eventEnd.diff(eventStart, 'minutes') / this.currentSlotDuration);
    let res = totalSlots > 0;
    // Modified "<" instead of "<=" to check the real slots and clone moment object to avoid mutate the original one.
    for (let init = 0; init < totalSlots; init++) {
      const slotStart = eventStart.clone().add(this.currentSlotDuration * init, 'minutes');
      const slotEnd = slotStart.clone().add(this.currentSlotDuration, 'minutes');
      res = res && this.existsSlotCategoryInArray(backgroundEventByResource, slotStart, slotEnd);
    }
    this.slotPriceCategoryCriterionError = !res;
    return res;
  }

  existsSlotCategoryInArray(
    eventArray: ParsedEvent[],
    eventSlotStart: Moment,
    eventSlotEnd: Moment,
  ) {
    const slotsThatMatch = eventArray.filter(slotEvent => {
      const slotStart = moment(slotEvent.start);
      const slotEnd = moment(slotEvent.end);
      return slotStart.isSameOrBefore(eventSlotStart) && slotEnd.isSameOrAfter(eventSlotEnd);
    });

    return slotsThatMatch.length > 0;
  }

  checkConflicCriterion(start: Moment, end: Moment, resourceId: number | string): boolean {
    const eventsInConflict = this.allLoadedEventsByResource(resourceId).filter(event => {
      const eventStart = moment(event.start);
      const eventEnd = moment(event.end);

      // event         [-----] OR  [-----]
      // slot       [-----]    OR  [-----]
      const conflictType1 =
        eventStart.isSameOrAfter(start) && eventStart.isBefore(end) && eventEnd.isSameOrAfter(end);
      if (conflictType1) {
        return true;
      }

      // event   [-----]       OR  [-------]
      // slot       [-----]    OR    [-----]
      const conflictType2 =
        eventEnd.isSameOrBefore(end) && eventEnd.isAfter(start) && eventStart.isSameOrBefore(start);
      if (conflictType2) {
        return true;
      }
      // event   [-----------]
      // slot       [-----]
      const conflictType3 = eventStart.isBefore(start) && eventEnd.isAfter(end);
      if (conflictType3) {
        return true;
      }
      // event      [----]
      // slot     [---------]
      const conflictType4 = eventStart.isAfter(start) && eventEnd.isBefore(end);
      if (conflictType4) {
        return true;
      }
      return false;
    });
    this.conflictCriterionError = eventsInConflict.length ? true : false;
    return !eventsInConflict.length;
  }

  startHourCriterionErrorMessage = null;
  checkStartHourCriterion(start: Moment) {
    const startHourMinutes = start.minutes() + start.hours() * 60;
    const optionStartTime = moment(this.todayStartTime, 'hh:mm');
    const optionStartTimeMinutes = optionStartTime.minutes() + optionStartTime.hours() * 60;
    const isValid = optionStartTimeMinutes <= startHourMinutes;
    this.startHourCriterionError = !isValid;

    if (!isValid) {
      this.startHourCriterionErrorMessage = this.todayStartTime;
    } else {
      this.startHourCriterionErrorMessage = null;
    }

    return isValid;
  }

  checkDurationCriterion(duration: number) {
    const validDuration = !!this.mergedBookingOptions.allowed_durations.find(
      allowed_duration => allowed_duration === duration,
    );
    this.durationCriterionError = !validDuration;
    return validDuration;
  }

  parseBookingDataToCalendarTmpEvent(event: PublicBookingFormData): ParsedEvent {
    return {
      id: null,
      title: this.$t('global.booking_events.new_booking'),
      start: this.formatDateTime(event.date, event.start_time),
      end: this.formatDateTime(event.date, event.end_time),
      backgroundColor: CalendarHelper.PROFILE_EVENT_TMP_BG_COLOR,
      borderColor: CalendarHelper.PROFILE_EVENT_TMP_BORDER_COLOR,
      resourceId: `${event.resource_id}`,
      className: ['tmp-event'],
      event_type: 'booking',
      booking_type: 'tmp',
      // display: 'background',
    };
  }

  parseBookingEventToCalendarOwnEvent(event: BookingEvent): ParsedEvent {
    return {
      id: event.id,
      title: event.name || this.$t('global.booking_events.my_booking'),
      start: this.formatDateTime(event.date, event.start_time),
      end: this.formatDateTime(event.date, event.end_time),
      backgroundColor: CalendarHelper.PROFILE_EVENT_BG_COLOR,
      borderColor: CalendarHelper.PROFILE_EVENT_BORDER_COLOR,
      resourceId: `${event.resource_id}`,
      className: ['my-event'],
      resource: event.resource,
      event_type: 'booking',
      discipline: event.discipline,
      // display: 'background',
    };
  }

  generateBookingData(
    start: Moment,
    end: Moment,
    resourceId: number | string,
    resource = null,
  ): PublicBookingFormData {
    return {
      status: null,
      start_time: start.format('HH:mm:ss'),
      date: start.format('YYYY-MM-DD'),
      end_time: end.format('HH:mm:ss'),
      resource_id: Number(resourceId),
      discipline_id: this.disciplineId,
      subscription_category_id: this.subscriptionCategoryId,
      extra: {
        resource: resource,
        discipline: this.selectedDiscipline,
      },
    };
  }

  generateBookingEventCancelData(
    id: number,
    start: Moment,
    end: Moment,
    resourceId: number,
    resource: string,
    discipline: string,
    isMultiplayer: boolean,
    numberOfPlayers: number,
  ): PublicBookingEventData {
    return {
      id: id,
      start_time: start.format('HH:mm:ss'),
      date: start.format('YYYY-MM-DD'),
      end_time: end.format('HH:mm:ss'),
      resourceId: resourceId,
      resource: resource,
      discipline: discipline,
      is_multiplayer: isMultiplayer,
      number_of_players: numberOfPlayers,
    };
  }

  cleanTmpEvents() {
    this.tmpEvents = [];
  }

  /**This function calculate the hour when the event should start depends on the step duration. The point of reference is the option start time */
  private calculateStart(dateStr: string | moment.Moment): Moment {
    const start = moment(dateStr);
    const dayBefore = moment(dateStr).subtract(1, 'days');
    const optionStartTime = moment(start.format('YYYY-MM-DD') + ' ' + this.todayStartTime);

    const diff = start.diff(optionStartTime, 'minutes');
    // NOTE: If the booking is done between a day of time change (dayLight Saving)
    // There will be less or more minutes during the night (start of the day)
    // we need to adjust this to make it works properly
    const dayLightSavingDiff = 24 * 60 - start.diff(dayBefore, 'minutes');
    const optionStartTimeMinutes = optionStartTime.minutes() + optionStartTime.hours() * 60;

    // Note that the following lines of code were first contained inside an if condition to check that "diff >= 0"
    // That condition was not respected when the time change is from summer to winter (removing an hour)
    // which was causing an issue. That's the condition has simply been removed.
    const steps = Math.floor((diff - dayLightSavingDiff) / this.mergedBookingOptions.step_duration);
    start.startOf('day');
    start.add(optionStartTimeMinutes + steps * this.mergedBookingOptions.step_duration, 'minutes');

    return start;
  }

  onEventClicked(event) {
    const bookingEvent = event.event;
    if (bookingEvent.classNames.includes('my-event')) {
      this.bookingEventData = this.generateBookingEventCancelData(
        bookingEvent.id,
        moment(bookingEvent.start),
        moment(bookingEvent.end),
        bookingEvent.extendedProps.resource?.id,
        bookingEvent.extendedProps.resource?.name,
        this.selectedDiscipline.name,
        bookingEvent.extendedProps.isMultiplayer,
        bookingEvent.extendedProps.numberOfPlayers,
      );
      this.showBookingEventDetailModal();
    }
  }

  async getSlotPricesCategories(): Promise<void> {
    if (!this.eventQuery.date) {
      console.warn('No date selected');
      return;
    }
    const params = {
      date: this.eventQuery.date,
      with_resources: true,
    };
    const res = await this.$bpcApi
      .get(this.$bpcApiRouteHelper.getEntityPath('schedule-slot-ranges') + '/ranges-by-date', {
        params: params,
      })
      .toPromise();
    this.backgroundEvents = this.addSlotCategoriesAsBackgroundEvents(res.data, params.date);
  }

  addSlotCategoriesAsBackgroundEvents(
    scheduleSlotRangeCollection: ScheduleSlotRange[],
    date: string,
  ): ParsedEvent[] {
    return scheduleSlotRangeCollection.reduce((backgroundEvents: ParsedEvent[], slot) => {
      const onlineBookingResourceIds = slot.online_booking_resources;

      const onlineBookingSchedules = this.addBackgroundEventsFromSlot(
        slot,
        onlineBookingResourceIds,
        date,
      );

      backgroundEvents = backgroundEvents.concat(onlineBookingSchedules);
      return backgroundEvents;
    }, []);
  }

  addBackgroundEventsFromSlot(
    slot: ScheduleSlotRange,
    resourceIds: number[],
    date: string,
  ): ParsedEvent[] {
    return resourceIds.map(resourceId => {
      return {
        start: this.formatDateTime(date, slot.start_time),
        end: this.formatDateTime(date, slot.end_time),
        // NOTE if we want to add slot colors
        backgroundColor: CalendarHelper.PROFILE_EVENT_FREE_SLOT_BG_COLOR, // green //`${slot.color}`,
        borderColor: CalendarHelper.PROFILE_EVENT_FREE_SLOT_BORDER_COLOR, // green //`${slot.color}`,
        display: 'background',
        event_type: 'slot',
        resourceId: resourceId,
      };
    });
  }

  @Watch('selectedDate')
  async onSelectedDateChangeRefreshSlotCategories() {
    await this.getSlotPricesCategories();
  }
}
